import { supabase } from '../supabase/supabaseClient';
import { createOrUpdateArtifact } from '../artifacts/artifactService';
import { tickerDetectionService } from '../stock/tickerDetectionService';
import { conversationContextService, MessageContextMetadata } from './conversationContextService';

export interface Message {
  id: string;
  group_id: string;
  user_id: string;
  content: string;
  created_at: string;
  reply_to_message_id: string | null;
  metadata:
    | MessageContextMetadata
    | {
        extracted_tickers?: string[];
        manual_tickers?: string[];
        inherited_tickers?: string[];
        sentiment?: string;
        // AI-specific metadata
        isAI?: boolean;
        isTyping?: boolean;
        query?: string;
        tickers?: string[];
        citations?: string[];
        // User mentions
        mentioned_users?: Array<{ user_id: string; name: string; position: number }>;
      };
  user?: {
    id: string;
    name: string;
    email: string;
    avatar_url: string | null;
  };
  // Computed fields for UI
  replied_to_message?: {
    id: string;
    user_name: string;
    content: string;
  };
}

export interface MessageReactionSummary {
  counts: Record<string, number>;
  mine: string[];
}

export interface LinkPreview {
  id: string;
  url: string;
  title: string | null;
  description: string | null;
  image_url: string | null;
  site_name: string | null;
  fetched_at?: string;
  status?: number | null;
}

/**
 * Fetch messages for a group
 */
export const getGroupMessages = async (
  groupId: string,
  limit: number = 500
): Promise<{ data: Message[] | null; error: string | null }> => {
  try {
    console.log(`📬 [ChatService] Fetching messages for group: ${groupId} (limit: ${limit})`);
    const startTime = Date.now();

    const { data: messages, error } = await supabase
      .from('messages')
      .select(
        `
        id,
        group_id,
        user_id,
        content,
        created_at,
        reply_to_message_id,
        metadata,
        mentioned_users,
        profiles:user_id (
          id,
          name,
          email,
          avatar_url
        ),
        replied_message:reply_to_message_id (
          id,
          content,
          user_id,
          profiles:user_id (
            name
          )
        )
      `
      )
      .eq('group_id', groupId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error(`❌ [ChatService] Error fetching messages for group ${groupId}:`, error);
      return { data: null, error: error.message };
    }

    // Transform to match our Message interface
    const formattedMessages = (messages || []).map((msg: any) => ({
      id: msg.id,
      group_id: msg.group_id,
      user_id: msg.user_id,
      content: msg.content,
      created_at: msg.created_at,
      reply_to_message_id: msg.reply_to_message_id,
      metadata: msg.metadata || {},
      user: msg.profiles
        ? {
            id: msg.profiles.id,
            name: msg.profiles.name,
            email: msg.profiles.email,
            avatar_url: msg.profiles.avatar_url,
          }
        : undefined,
      replied_to_message:
        msg.replied_message && msg.replied_message.id
          ? {
              id: msg.replied_message.id,
              user_name: msg.replied_message.profiles?.name || 'Unknown',
              content: msg.replied_message.content,
            }
          : undefined,
    }));

    const fetchTime = Date.now() - startTime;
    console.log(`✅ [ChatService] Fetched ${formattedMessages.length} messages in ${fetchTime}ms`);

    // Log ticker mentions if any
    const tickerMentions = formattedMessages.filter(
      m => m.metadata.extracted_tickers && m.metadata.extracted_tickers.length > 0
    );
    if (tickerMentions.length > 0) {
      const uniqueTickers = [
        ...new Set(tickerMentions.flatMap(m => m.metadata.extracted_tickers || [])),
      ];
      console.log(
        `📊 [ChatService] Found ${tickerMentions.length} messages with tickers: ${uniqueTickers.join(', ')}`
      );
    }

    // Reverse array to display oldest-first in UI (we fetched newest-first from DB)
    return { data: formattedMessages.reverse(), error: null };
  } catch (error) {
    console.error(
      `❌ [ChatService] Unexpected error fetching messages for group ${groupId}:`,
      error
    );
    return { data: null, error: 'An unexpected error occurred' };
  }
};

/**
 * Fetch earlier messages for pagination (before a given timestamp)
 */
export const getEarlierMessages = async (
  groupId: string,
  beforeTimestamp: string,
  limit: number = 50
): Promise<{ data: Message[] | null; error: string | null; hasMore: boolean }> => {
  try {
    console.log(
      `📬 [ChatService] Fetching earlier messages before: ${beforeTimestamp} (limit: ${limit})`
    );
    const startTime = Date.now();

    const { data: messages, error } = await supabase
      .from('messages')
      .select(
        `
        id,
        group_id,
        user_id,
        content,
        created_at,
        reply_to_message_id,
        metadata,
        mentioned_users,
        profiles:user_id (
          id,
          name,
          email,
          avatar_url
        ),
        replied_message:reply_to_message_id (
          id,
          content,
          user_id,
          profiles:user_id (
            name
          )
        )
      `
      )
      .eq('group_id', groupId)
      .lt('created_at', beforeTimestamp)
      .order('created_at', { ascending: false })
      .limit(limit + 1); // Fetch one extra to check if there are more

    if (error) {
      console.error(`❌ [ChatService] Error fetching earlier messages:`, error);
      return { data: null, error: error.message, hasMore: false };
    }

    // Check if there are more messages
    const hasMore = messages && messages.length > limit;
    const messagesToReturn = hasMore ? messages.slice(0, limit) : messages || [];

    // Transform to match our Message interface
    const formattedMessages = messagesToReturn.map((msg: any) => ({
      id: msg.id,
      group_id: msg.group_id,
      user_id: msg.user_id,
      content: msg.content,
      created_at: msg.created_at,
      reply_to_message_id: msg.reply_to_message_id,
      metadata: msg.metadata || {},
      user: msg.profiles
        ? {
            id: msg.profiles.id,
            name: msg.profiles.name,
            email: msg.profiles.email,
            avatar_url: msg.profiles.avatar_url,
          }
        : undefined,
      replied_to_message:
        msg.replied_message && msg.replied_message.id
          ? {
              id: msg.replied_message.id,
              user_name: msg.replied_message.profiles?.name || 'Unknown',
              content: msg.replied_message.content,
            }
          : undefined,
    }));

    const fetchTime = Date.now() - startTime;
    console.log(
      `✅ [ChatService] Fetched ${formattedMessages.length} earlier messages in ${fetchTime}ms (hasMore: ${hasMore})`
    );

    // Reverse array to display oldest-first in UI (we fetched newest-first from DB)
    return { data: formattedMessages.reverse(), error: null, hasMore };
  } catch (error) {
    console.error(`❌ [ChatService] Unexpected error fetching earlier messages:`, error);
    return { data: null, error: 'An unexpected error occurred', hasMore: false };
  }
};

/**
 * Send a new message to a group with INSTANT delivery and background enrichment
 *
 * PHASE 1 (FAST): Insert message with minimal data (100-200ms)
 * PHASE 2 (BACKGROUND): Enrich with tickers, context, and artifacts
 *
 * @param groupId - The group to send the message to
 * @param content - The message content
 * @param options - Optional parameters for context
 * @param options.manualTickers - User-specified tickers (manual tagging)
 * @param options.replyToMessageId - Message being replied to (for threading)
 * @param options.customMetadata - Custom metadata for AI messages, etc.
 */
export const sendMessage = async (
  groupId: string,
  content: string,
  options?: {
    manualTickers?: string[];
    replyToMessageId?: string | null;
    customMetadata?: Record<string, any>;
  }
): Promise<{ data: Message | null; error: string | null }> => {
  try {
    console.log(
      `📤 [ChatService] Sending message to group ${groupId}: "${content.substring(0, 50)}${content.length > 50 ? '...' : ''}"`
    );
    if (options?.manualTickers) {
      console.log(`🏷️ [ChatService] Manual tags: ${options.manualTickers.join(', ')}`);
    }
    if (options?.replyToMessageId) {
      console.log(`🔗 [ChatService] Replying to message: ${options.replyToMessageId}`);
    }

    const startTime = Date.now();

    // ============================================================================
    // PHASE 1: FAST INSERT - Get message into DB ASAP (Target: <200ms)
    // ============================================================================

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      console.error(`❌ [ChatService] Cannot send message - user not authenticated`);
      return { data: null, error: 'User not authenticated' };
    }

    // Minimal metadata for fast insert
    const minimalMetadata = options?.customMetadata || {};

    // Add manual tickers if provided (no processing needed)
    if (options?.manualTickers && options.manualTickers.length > 0) {
      minimalMetadata.manual_tickers = options.manualTickers;
      minimalMetadata.extracted_tickers = options.manualTickers; // Treat manual as extracted for immediate filtering
    }

    // Extract mentioned_users from customMetadata if present
    const mentionedUsers = options?.customMetadata?.mentioned_users || null;

    // FAST INSERT: Only essential fields
    const { data: message, error } = await supabase
      .from('messages')
      .insert({
        group_id: groupId,
        user_id: user.id,
        content,
        reply_to_message_id: options?.replyToMessageId || null,
        metadata: minimalMetadata as any,
        mentioned_users: mentionedUsers,
      })
      .select(
        `
        id,
        group_id,
        user_id,
        content,
        created_at,
        reply_to_message_id,
        metadata,
        mentioned_users,
        profiles:user_id (
          id,
          name,
          email,
          avatar_url
        )
      `
      )
      .single();

    if (error) {
      console.error(`❌ [ChatService] Error sending message to group ${groupId}:`, error);
      return { data: null, error: error.message };
    }

    const insertTime = Date.now() - startTime;
    console.log(`✅ [ChatService] Message inserted in ${insertTime}ms (ID: ${message.id})`);

    // Format message for immediate return
    const formattedMessage: Message = {
      id: message.id,
      group_id: message.group_id,
      user_id: message.user_id,
      content: message.content,
      created_at: message.created_at,
      reply_to_message_id: message.reply_to_message_id,
      metadata: (message.metadata || {}) as any,
      user: message.profiles
        ? {
            id: message.profiles.id,
            name: message.profiles.name,
            email: message.profiles.email,
            avatar_url: message.profiles.avatar_url,
          }
        : undefined,
    };

    // ============================================================================
    // PHASE 2: BACKGROUND ENRICHMENT - Add intelligence without blocking UI
    // ============================================================================

    // Fire and forget - enrich message in background
    enrichMessageInBackground(message.id, groupId, content, {
      manualTickers: options?.manualTickers,
      replyToMessageId: options?.replyToMessageId,
      customMetadata: options?.customMetadata,
    }).catch(error => {
      console.error(
        `❌ [ChatService] Background enrichment failed for message ${message.id}:`,
        error
      );
    });

    console.log(`⚡ [ChatService] Message returned in ${insertTime}ms, enriching in background...`);

    // ============================================================================
    // PHASE 3: SEND PUSH NOTIFICATIONS (Fire and forget)
    // ============================================================================
    sendPushNotifications(message, groupId, user, mentionedUsers).catch(error => {
      console.error(`❌ [ChatService] Push notification failed:`, error);
      // Don't block message send if notifications fail
    });

    return { data: formattedMessage, error: null };
  } catch (error) {
    console.error(`❌ [ChatService] Unexpected error sending message to group ${groupId}:`, error);
    return { data: null, error: 'An unexpected error occurred' };
  }
};

/**
 * Background enrichment: Add tickers, context, and artifacts AFTER message is sent
 * This runs asynchronously and updates the message metadata when complete
 */
async function enrichMessageInBackground(
  messageId: string,
  groupId: string,
  content: string,
  options?: {
    manualTickers?: string[];
    replyToMessageId?: string | null;
    customMetadata?: Record<string, any>;
  }
): Promise<void> {
  const enrichStartTime = Date.now();
  console.log(`🔮 [ChatService] Starting background enrichment for message ${messageId}`);

  try {
    // STEP 1: Extract tickers from ALL messages (no pre-filtering for maximum accuracy)
    console.log(`🔍 [ChatService] Background: Detecting tickers in message`);
    const tickerResult = await tickerDetectionService.extractMultipleTickers(content);
    const explicitTickers = tickerResult.tickers;

    if (explicitTickers.length > 0) {
      console.log(`💬 [ChatService] Background: Found tickers: ${explicitTickers.join(', ')}`);
    }

    // STEP 2 & 3: Run context analysis and reply context fetch in parallel
    const [context, replyMsg] = await Promise.all([
      // Context analysis
      conversationContextService.analyzeMessageContext(groupId, content, {
        manualTickers: options?.manualTickers,
        replyToMessageId: options?.replyToMessageId || null,
        explicitTickers,
      }),

      // Reply context (only if replying)
      options?.replyToMessageId
        ? supabase
            .from('messages')
            .select(
              `
          content,
          profiles:user_id (name)
        `
            )
            .eq('id', options.replyToMessageId)
            .single()
        : Promise.resolve({ data: null, error: null }),
    ]);

    console.log(
      `🧠 [ChatService] Background: Context analysis complete - ${context.tickers.length} tickers, confidence: ${(context.confidence * 100).toFixed(0)}%`
    );

    // Format reply context
    let replyContext: { user_name: string; content_preview: string } | undefined;
    if (replyMsg.data) {
      replyContext = {
        user_name: (replyMsg.data.profiles as any)?.name || 'Unknown',
        content_preview: replyMsg.data.content.substring(0, 50),
      };
    }

    // STEP 4: Format enriched metadata
    const enrichedMetadata = conversationContextService.formatContextMetadata(
      context,
      options?.replyToMessageId || null,
      replyContext
    );

    // Merge with custom metadata (preserve AI-specific fields)
    const finalMetadata = options?.customMetadata
      ? { ...enrichedMetadata, ...options.customMetadata }
      : enrichedMetadata;

    // STEP 5: UPDATE message with enriched metadata
    console.log(`📝 [ChatService] Updating message ${messageId} with metadata:`, {
      extracted_tickers: finalMetadata.extracted_tickers,
      manual_tickers: finalMetadata.manual_tickers,
      inherited_tickers: finalMetadata.inherited_tickers,
      source: finalMetadata.context_source,
    });

    const {
      data: updateResult,
      error: updateError,
      count,
    } = await supabase
      .from('messages')
      .update({ metadata: finalMetadata as any })
      .eq('id', messageId)
      .select();

    console.log(`📊 [ChatService] UPDATE query executed:`, {
      error: updateError,
      rowsAffected: updateResult?.length || 0,
      count: count,
      returnedData: updateResult ? 'yes' : 'no',
    });

    if (updateError) {
      console.error(`❌ [ChatService] Failed to update message metadata:`, updateError);
      throw updateError;
    }

    if (!updateResult || updateResult.length === 0) {
      console.error(
        `⚠️ [ChatService] UPDATE succeeded but affected 0 rows - RLS policy may be blocking!`
      );
    }

    const enrichTime = Date.now() - enrichStartTime;
    console.log(`✅ [ChatService] Message ${messageId} enriched in ${enrichTime}ms`);
    console.log(
      `📊 [ChatService] Final metadata has ${finalMetadata.extracted_tickers?.length || 0} extracted tickers`
    );

    // STEP 5.1: Link previews (fetch + map). Non-blocking errors.
    try {
      const urls = Array.from(new Set((content.match(/https?:\/\/[^\s]+/g) || []).slice(0, 2)));
      for (const url of urls) {
        try {
          const { data: fnRes, error: fnErr } = await supabase.functions.invoke('url-metadata', {
            body: { url },
          });
          if (!fnErr && fnRes?.preview?.id) {
            await supabase
              .from('message_link_preview')
              .insert({ message_id: messageId, preview_id: fnRes.preview.id });
          }
        } catch (e) {
          console.warn('[ChatService] url-metadata failed', e);
        }
      }
    } catch (e) {
      console.warn('[ChatService] preview extraction failed', e);
    }

    // STEP 6: Create/update stock artifacts for all tickers (fire and forget)
    if (context.tickers.length > 0) {
      console.log(
        `🎨 [ChatService] Creating artifacts in background: ${context.tickers.join(', ')}`
      );

      // Check if this is an AI message
      const isAIMessage = options?.customMetadata?.isAI === true;

      Promise.all(
        context.tickers.map(async ticker => {
          try {
            const cryptoTickers = [
              'BTC',
              'ETH',
              'USDT',
              'BNB',
              'SOL',
              'ADA',
              'DOGE',
              'XRP',
              'DOT',
              'MATIC',
            ];
            const assetType = cryptoTickers.includes(ticker) ? 'crypto' : 'stock';

            await createOrUpdateArtifact(groupId, ticker, assetType, {
              is_ai_recommended: isAIMessage,
            });
            console.log(`✅ [ChatService] Artifact created for ${ticker}`);
          } catch (error) {
            console.error(`❌ [ChatService] Artifact creation failed for ${ticker}:`, error);
          }
        })
      ).catch(error => {
        console.error('❌ [ChatService] Artifact batch failed:', error);
      });
    }
  } catch (error) {
    console.error(`❌ [ChatService] Background enrichment failed for message ${messageId}:`, error);
    throw error;
  }
}

/**
 * Subscribe to new messages in a group (Real-time)
 * Handles both INSERT (new messages) and UPDATE (metadata enrichment) events
 */
export const subscribeToMessages = (
  groupId: string,
  onMessage: (message: Message) => void,
  onMessageUpdate?: (message: Message) => void
) => {
  console.log(`🔌 [ChatService] Subscribing to real-time messages for group: ${groupId}`);

  const fetchFullMessage = async (messageId: string): Promise<Message | null> => {
    const { data, error } = await supabase
      .from('messages')
      .select(
        `
        id,
        group_id,
        user_id,
        content,
        created_at,
        reply_to_message_id,
        metadata,
        mentioned_users,
        profiles:user_id (
          id,
          name,
          email,
          avatar_url
        ),
        replied_message:reply_to_message_id (
          id,
          content,
          user_id,
          profiles:user_id (
            name
          )
        )
      `
      )
      .eq('id', messageId)
      .single();

    if (error) {
      console.error(`❌ [ChatService] Error fetching full message for real-time update:`, error);
      return null;
    }

    if (!data) return null;

    return {
      id: data.id,
      group_id: data.group_id,
      user_id: data.user_id,
      content: data.content,
      created_at: data.created_at,
      reply_to_message_id: data.reply_to_message_id,
      metadata: (data.metadata || {}) as any,
      user: data.profiles
        ? {
            id: data.profiles.id,
            name: data.profiles.name,
            email: data.profiles.email,
            avatar_url: data.profiles.avatar_url,
          }
        : undefined,
      replied_to_message:
        data.replied_message && data.replied_message.id
          ? {
              id: data.replied_message.id,
              user_name: data.replied_message.profiles?.name || 'Unknown',
              content: data.replied_message.content,
            }
          : undefined,
    };
  };

  const channel = supabase
    .channel(`messages:${groupId}`)
    // Listen for new messages (INSERT)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `group_id=eq.${groupId}`,
      },
      async payload => {
        console.log(
          `🔔 [ChatService] Real-time INSERT received for group ${groupId} - message ID: ${payload.new.id}`
        );

        const formattedMessage = await fetchFullMessage(payload.new.id);
        if (formattedMessage) {
          console.log(
            `✅ [ChatService] Real-time message processed from ${formattedMessage.user?.name || 'unknown'}: "${formattedMessage.content.substring(0, 30)}..."`
          );
          onMessage(formattedMessage);
        }
      }
    )
    // Listen for message updates (metadata enrichment)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'messages',
        filter: `group_id=eq.${groupId}`,
      },
      async payload => {
        console.log(
          `🔄 [ChatService] Real-time UPDATE received for group ${groupId} - message ID: ${payload.new.id} (likely metadata enrichment)`
        );

        const formattedMessage = await fetchFullMessage(payload.new.id);
        if (formattedMessage && onMessageUpdate) {
          console.log(
            `✅ [ChatService] Message metadata updated - ${formattedMessage.metadata.extracted_tickers?.length || 0} tickers detected`
          );
          onMessageUpdate(formattedMessage);
        }
      }
    )
    .subscribe(status => {
      console.log(`📡 [ChatService] Subscription status changed for group ${groupId}:`, status);
    });

  return () => {
    console.log(`🔌 [ChatService] Unsubscribing from real-time messages for group: ${groupId}`);
    supabase.removeChannel(channel);
  };
};

/** Toggle a reaction (emoji) for the current user on a message */
export const toggleMessageReaction = async (
  messageId: string,
  emoji: string
): Promise<{ action: 'added' | 'removed' | null; error: string | null }> => {
  try {
    const { data, error } = await supabase.rpc('message_react', {
      p_message_id: messageId,
      p_emoji: emoji,
    });
    if (error) return { action: null, error: error.message };
    const action =
      (data as string) === 'added' || (data as string) === 'removed'
        ? (data as 'added' | 'removed')
        : null;
    return { action, error: null };
  } catch (e: any) {
    return { action: null, error: e?.message || 'Failed to react' };
  }
};

/** Fetch reactions for messages and compute counts + mine flags */
export const getReactionsForMessages = async (
  messageIds: string[],
  currentUserId: string
): Promise<Record<string, MessageReactionSummary>> => {
  if (messageIds.length === 0) return {};
  const { data, error } = await supabase
    .from('message_reaction')
    .select('message_id, emoji, user_id')
    .in('message_id', messageIds);

  const map: Record<string, MessageReactionSummary> = {};
  if (error || !data) return map;
  for (const r of data as any[]) {
    if (!map[r.message_id]) map[r.message_id] = { counts: {}, mine: [] };
    map[r.message_id].counts[r.emoji] = (map[r.message_id].counts[r.emoji] || 0) + 1;
    if (r.user_id === currentUserId && !map[r.message_id].mine.includes(r.emoji)) {
      map[r.message_id].mine.push(r.emoji);
    }
  }
  return map;
};

/** Fetch link previews for messages */
export const getPreviewsForMessages = async (
  messageIds: string[]
): Promise<Record<string, LinkPreview[]>> => {
  if (messageIds.length === 0) return {};
  const { data: mappings } = await supabase
    .from('message_link_preview')
    .select('message_id, preview_id')
    .in('message_id', messageIds);
  const byMessage: Record<string, string[]> = {};
  for (const m of (mappings as any[]) || []) {
    if (!byMessage[m.message_id]) byMessage[m.message_id] = [];
    byMessage[m.message_id].push(m.preview_id);
  }
  const allIds = Array.from(new Set(Object.values(byMessage).flat()));
  if (allIds.length === 0) return {};
  const { data: previews } = await supabase
    .from('link_preview_cache')
    .select('id, url, title, description, image_url, site_name, fetched_at, status')
    .in('id', allIds);
  const byId = new Map<string, LinkPreview>();
  for (const p of (previews as any[]) || []) byId.set(p.id, p as LinkPreview);
  const result: Record<string, LinkPreview[]> = {};
  for (const [msgId, ids] of Object.entries(byMessage)) {
    result[msgId] = ids.map(id => byId.get(id)).filter(Boolean) as LinkPreview[];
  }
  return result;
};

/**
 * Poll for message enrichment (fallback when real-time fails)
 *
 * This is a scalable alternative to real-time UPDATE subscriptions.
 * Only polls the specific message that was just sent, not all messages.
 *
 * @param messageId - The message ID to check for enrichment
 * @returns The updated message with enriched metadata, or null if not found
 */
/**
 * Send push notifications for a new message
 * Calls Supabase Edge Function to handle notification delivery
 */
async function sendPushNotifications(
  message: any,
  groupId: string,
  user: any,
  mentionedUsers: any[]
): Promise<void> {
  try {
    console.log(`🔔 [ChatService] Triggering push notifications for message ${message.id}`);

    // Get group name for notification
    const { data: group } = await supabase.from('groups').select('name').eq('id', groupId).single();

    if (!group) {
      console.warn(`⚠️ [ChatService] Group not found for notifications: ${groupId}`);
      return;
    }

    // Call Edge Function
    const { data, error } = await supabase.functions.invoke('send-message-notification', {
      body: {
        messageId: message.id,
        groupId: groupId,
        groupName: group.name,
        senderId: user.id,
        senderName: message.profiles?.name || user.email,
        content: message.content,
        mentionedUsers: mentionedUsers || [],
      },
    });

    if (error) {
      console.error(`❌ [ChatService] Edge Function error:`, error);
      throw error;
    }

    console.log(`✅ [ChatService] Push notifications sent:`, data);
  } catch (error) {
    console.error(`❌ [ChatService] Push notification error:`, error);
    throw error;
  }
}

/**
 * Poll for message enrichment (fallback when real-time fails)
 *
 * This is a scalable alternative to real-time UPDATE subscriptions.
 * Only polls the specific message that was just sent, not all messages.
 *
 * @param messageId - The message ID to check for enrichment
 * @returns The updated message with enriched metadata, or null if not found
 */
export const pollMessageEnrichment = async (
  messageId: string
): Promise<{ data: Message | null; error: string | null }> => {
  try {
    console.log(`🔄 [ChatService] Polling for enrichment of message: ${messageId}`);

    const { data, error } = await supabase
      .from('messages')
      .select(
        `
        id,
        group_id,
        user_id,
        content,
        created_at,
        reply_to_message_id,
        metadata,
        mentioned_users,
        profiles:user_id (
          id,
          name,
          email,
          avatar_url
        ),
        replied_message:reply_to_message_id (
          id,
          content,
          user_id,
          profiles:user_id (
            name
          )
        )
      `
      )
      .eq('id', messageId)
      .single();

    if (error) {
      console.error(`❌ [ChatService] Error polling message enrichment:`, error);
      return { data: null, error: error.message };
    }

    if (!data) {
      console.warn(`⚠️ [ChatService] Message ${messageId} not found during polling`);
      return { data: null, error: 'Message not found' };
    }

    const formattedMessage: Message = {
      id: data.id,
      group_id: data.group_id,
      user_id: data.user_id,
      content: data.content,
      created_at: data.created_at,
      reply_to_message_id: data.reply_to_message_id,
      metadata: (data.metadata || {}) as any,
      user: data.profiles
        ? {
            id: data.profiles.id,
            name: data.profiles.name,
            email: data.profiles.email,
            avatar_url: data.profiles.avatar_url,
          }
        : undefined,
      replied_to_message:
        data.replied_message && data.replied_message.id
          ? {
              id: data.replied_message.id,
              user_name: data.replied_message.profiles?.name || 'Unknown',
              content: data.replied_message.content,
            }
          : undefined,
    };

    // Debug: Log the metadata we received
    console.log(`🔍 [ChatService] Polled metadata for ${messageId}:`, {
      extracted_tickers: formattedMessage.metadata.extracted_tickers,
      manual_tickers: formattedMessage.metadata.manual_tickers,
      inherited_tickers: formattedMessage.metadata.inherited_tickers,
      context_source: formattedMessage.metadata.context_source,
      metadata_keys: Object.keys(formattedMessage.metadata),
    });

    // Check if message has been enriched
    const hasEnrichedTickers = formattedMessage.metadata.extracted_tickers?.length > 0;

    if (hasEnrichedTickers) {
      console.log(
        `✅ [ChatService] Message enriched with ${formattedMessage.metadata.extracted_tickers?.length} tickers: ${formattedMessage.metadata.extracted_tickers?.join(', ')}`
      );
    } else {
      console.log(`⏳ [ChatService] Message not yet enriched (no tickers detected)`);
    }

    return { data: formattedMessage, error: null };
  } catch (error) {
    console.error(`❌ [ChatService] Unexpected error polling message enrichment:`, error);
    return { data: null, error: 'An unexpected error occurred' };
  }
};
